import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that calculates packaging units from m² input.
 * Updates the quantity selector automatically when m² is entered.
 * Also listens to quantity changes to update the m² display.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} m2Input - The m² input field
 * @property {HTMLInputElement} wastageInput - The wastage percentage input field
 * @property {HTMLElement} resultDisplay - The result display container
 * @property {HTMLElement} resultText - The result text element
 * @property {HTMLElement} totalText - The indicative total price element
 *
 * @extends {Component<Refs>}
 */
export class M2CalculatorComponent extends Component {
  // NB: bewust géén `requiredRefs`. Deze component verschijnt/verdwijnt per
  // variant (Section Rendering API), waardoor de inputvelden tijdens een
  // her-render kortstondig ontbreken. Een throwende requiredRefs-check crasht
  // dan de component; in plaats daarvan zijn de handlers hieronder null-safe.

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} Flag to prevent circular updates */
  #isUpdatingQuantity = false;

  /** @type {HTMLElement | null} */
  #quantitySelector = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Listen for variant updates to update reference_value
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // Find and observe the quantity selector for external changes
    // Use requestAnimationFrame to ensure other components are initialized first
    requestAnimationFrame(() => {
      if (signal.aborted) return;
      this.#setupQuantityObserver(signal);
      this.#showInitialM2Display();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Sets up observation of the quantity selector for external changes.
   * @param {AbortSignal} signal - Abort signal for cleanup
   */
  #setupQuantityObserver(signal) {
    // Find the quantity selector - try multiple locations
    const form = this.closest('form');
    const closestSection = this.closest('.shopify-section, dialog');
    
    this.#quantitySelector = /** @type {HTMLElement | null} */ (
      form?.querySelector('quantity-selector-component') ||
      closestSection?.querySelector('quantity-selector-component') ||
      document.querySelector(`quantity-selector-component[data-variant-id="${this.dataset.variantId}"]`)
    );

    // Listen for the QuantitySelectorUpdateEvent that the component dispatches
    // The event bubbles up from the input element
    // Listen on the section level to catch all quantity updates, since the event bubbles
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange, { signal });
    } else if (this.#quantitySelector) {
      // Fallback: listen directly on the quantity selector
      this.#quantitySelector.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange, { signal });
    }
  }

  /**
   * Shows the m² coverage under the quantity field from the start (1 box = X m²),
   * so the quantity is clearly labelled as boxes before the visitor touches anything.
   */
  #showInitialM2Display() {
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.#quantitySelector?.querySelector('input[name="quantity"]')
    );
    const quantity = parseInt(quantityInput?.value ?? '', 10);
    const m2PerPackage = parseFloat(this.dataset.quantityValue ?? '');
    if (!quantity || !m2PerPackage) return;
    this.#updateExternalM2Display(quantity, Math.round(quantity * m2PerPackage * 100) / 100);
  }

  /**
   * Shows the indicative total (boxes × price per box) in the result panel.
   * @param {number} packages - Number of boxes
   */
  #renderResult(packages) {
    const { resultDisplay, totalText } = this.refs;
    const priceCents = parseInt(this.dataset.priceCents ?? '', 10);
    if (totalText) {
      totalText.textContent = priceCents
        ? new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(
            (packages * priceCents) / 100
          )
        : '';
    }
    resultDisplay?.removeAttribute('hidden');
  }

  /**
   * Handles input events on the m² field or wastage field.
   * Calculates packaging units and updates quantity selector.
   */
  handleInput = () => {
    const { m2Input, wastageInput, resultText, resultDisplay } = this.refs;
    if (!m2Input || !resultText) return;
    const m2Value = parseFloat(m2Input.value);

    // Clear result if input is empty or invalid
    if (!m2Input.value || isNaN(m2Value) || m2Value <= 0) {
      resultText.textContent = '';
      resultDisplay?.setAttribute('hidden', '');
      return;
    }

    // Get wastage percentage (default to 10% if not set or invalid)
    // Check value first, then dataset, then default to 10
    let wastagePercentage = 10;
    if (wastageInput && wastageInput.value !== '' && !isNaN(parseFloat(wastageInput.value))) {
      wastagePercentage = parseFloat(wastageInput.value);
    } else if (wastageInput && wastageInput.dataset.wastagePercentage) {
      wastagePercentage = parseFloat(wastageInput.dataset.wastagePercentage);
    }
    
    // Ensure wastage percentage is valid (0-100)
    if (isNaN(wastagePercentage) || wastagePercentage < 0) {
      wastagePercentage = 10;
    }
    
    const wastageMultiplier = 1 + (wastagePercentage / 100);

    // Calculate m² with wastage
    const m2WithWastage = m2Value * wastageMultiplier;

    // Get quantity_value from data attribute (this is m² per package)
    const quantityValue = parseFloat(this.dataset.quantityValue);
    if (!quantityValue || isNaN(quantityValue) || quantityValue <= 0) {
      resultText.textContent = '';
      resultDisplay?.setAttribute('hidden', '');
      return;
    }

    // Calculate number of packages (always round up)
    const packages = Math.ceil(m2WithWastage / quantityValue);

    // Update result display with calculation details
    const m2WithWastageRounded = Math.round(m2WithWastage * 100) / 100; // Round to 2 decimals
    if (wastagePercentage > 0) {
      resultText.textContent = `${m2Value} m² + ${wastagePercentage}% snijverlies = ${m2WithWastageRounded} m² → U heeft ${packages} ${packages === 1 ? 'doos' : 'dozen'} nodig.`;
    } else {
      resultText.textContent = `U heeft ${packages} ${packages === 1 ? 'doos' : 'dozen'} nodig.`;
    }
    this.#renderResult(packages);

    // Find and update quantity selector
    // First try cached reference, then form, then section, then by variant ID
    const form = this.closest('form');
    const closestSection = this.closest('.shopify-section, dialog');
    const quantitySelector = /** @type {any} */ (
      this.#quantitySelector ||
      form?.querySelector('quantity-selector-component') ||
      closestSection?.querySelector('quantity-selector-component') ||
      document.querySelector(`quantity-selector-component[data-variant-id="${this.dataset.variantId}"]`)
    );

    if (quantitySelector && typeof quantitySelector.setValue === 'function') {
      // Set flag to prevent circular updates
      this.#isUpdatingQuantity = true;
      quantitySelector.setValue(packages.toString());
      // Trigger quantity change event to update button states
      if (typeof quantitySelector.onQuantityChange === 'function') {
        quantitySelector.onQuantityChange();
      }
      // Reset flag after a short delay to allow events to settle
      requestAnimationFrame(() => {
        this.#isUpdatingQuantity = false;
      });
    }

    // Update external m² display (e.g. near quantity selector)
    const totalM2 = packages * quantityValue;
    const totalM2Rounded = Math.round(totalM2 * 100) / 100;
    this.#updateExternalM2Display(packages, totalM2Rounded);
  };

  /**
   * Handles quantity selector changes (from external input).
   * Calculates the m² coverage based on number of boxes.
   * @param {QuantitySelectorUpdateEvent} event - The quantity selector update event
   */
  #handleQuantityChange = (event) => {
    // Ignore if we're the ones updating the quantity
    if (this.#isUpdatingQuantity) return;

    // Get the quantity value from the event detail
    const quantityValue = event.detail?.quantity;

    if (!quantityValue || isNaN(quantityValue) || quantityValue <= 0) return;

    // Get quantity_value from data attribute (this is m² per package)
    const m2PerPackage = parseFloat(this.dataset.quantityValue);
    if (!m2PerPackage || isNaN(m2PerPackage) || m2PerPackage <= 0) return;

    const { m2Input, wastageInput, resultText } = this.refs;
    if (!m2Input || !resultText) return;

    // Get wastage percentage
    let wastagePercentage = 10;
    if (wastageInput && wastageInput.value !== '' && !isNaN(parseFloat(wastageInput.value))) {
      wastagePercentage = parseFloat(wastageInput.value);
    } else if (wastageInput && wastageInput.dataset.wastagePercentage) {
      wastagePercentage = parseFloat(wastageInput.dataset.wastagePercentage);
    }
    if (isNaN(wastagePercentage) || wastagePercentage < 0) {
      wastagePercentage = 10;
    }

    // Calculate total m² covered by these boxes
    const totalM2 = quantityValue * m2PerPackage;
    const totalM2Rounded = Math.round(totalM2 * 100) / 100;

    // Calculate the m² needed (without wastage) that would result in this quantity
    // m² with wastage = m² × (1 + wastage%)
    // So: m² = total m² / (1 + wastage%)
    const wastageMultiplier = 1 + (wastagePercentage / 100);
    const m2WithoutWastage = totalM2 / wastageMultiplier;
    const m2WithoutWastageRounded = Math.round(m2WithoutWastage * 100) / 100;

    // Update the m² input field with the calculated value
    m2Input.value = m2WithoutWastageRounded.toString();

    // Update result display
    if (wastagePercentage > 0) {
      resultText.textContent = `${quantityValue} ${quantityValue === 1 ? 'doos' : 'dozen'} = ${totalM2Rounded} m² (geschikt voor ~${m2WithoutWastageRounded} m² + ${wastagePercentage}% snijverlies)`;
    } else {
      resultText.textContent = `${quantityValue} ${quantityValue === 1 ? 'doos' : 'dozen'} = ${totalM2Rounded} m²`;
    }
    this.#renderResult(quantityValue);

    // Update external m² display (e.g. near quantity selector)
    this.#updateExternalM2Display(quantityValue, totalM2Rounded);
  };

  /**
   * Updates external m² display elements (placed near quantity selector).
   * @param {number} quantity - Number of boxes
   * @param {number} totalM2 - Total m² covered
   */
  #updateExternalM2Display(quantity, totalM2) {
    const closestSection = this.closest('.shopify-section, dialog');
    const externalDisplays = closestSection?.querySelectorAll('[data-m2-quantity-display]') || [];
    
    externalDisplays.forEach((display) => {
      if (quantity > 0 && totalM2 > 0) {
        display.textContent = `${quantity === 1 ? 'doos' : 'dozen'} = ${totalM2} m²`;
        display.removeAttribute('hidden');
      } else {
        display.setAttribute('hidden', '');
      }
    });
  }

  /**
   * Handles variant update events.
   * Updates the reference_value and variant_id data attributes.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Check if this update is for the correct product
    if (event.detail.data.newProduct) {
      // Product changed, component will be re-rendered
      return;
    }

    // Get the new m2-calculator component from the updated HTML
    const newCalculator = event.detail.data.html?.querySelector('m2-calculator-component');
    if (!newCalculator) {
      // Component might not be visible for this variant (no m² unit)
      this.style.display = 'none';
      return;
    }

    // Update data attributes
    const newQuantityValue = newCalculator.dataset.quantityValue;
    const newVariantId = newCalculator.dataset.variantId;

    if (newQuantityValue) {
      this.dataset.quantityValue = newQuantityValue;
    }
    if (newVariantId) {
      this.dataset.variantId = newVariantId;
    }
    if (newCalculator.dataset.priceCents) {
      this.dataset.priceCents = newCalculator.dataset.priceCents;
    }

    // Show component if it was hidden
    this.style.display = '';

    // Recalculate if there's a value in the input
    const { m2Input } = this.refs;
    if (m2Input && m2Input.value) {
      this.handleInput();
    }
  };
}

if (!customElements.get('m2-calculator-component')) {
  customElements.define('m2-calculator-component', M2CalculatorComponent);
}

